import { Extension } from '@tiptap/core'
import { Plugin } from 'prosemirror-state'
import { DOMParser as ProseMirrorDOMParser, Fragment, Slice } from 'prosemirror-model'

/**
 * PasteCleanupExtension
 * - Strips font color from pasted HTML
 * - Removes <font color="..."> tags and inline `color:` styles/attributes
 */
const PasteCleanupExtension = Extension.create({
  name: 'pasteCleanup',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste(view, event: ClipboardEvent) {
            try {
              const html = event.clipboardData?.getData('text/html')
              // If there's no HTML, do not handle here
              if (!html) return false

              // Parse the incoming HTML using the browser DOM parser
              const parser = new window.DOMParser()
              const doc = parser.parseFromString(html, 'text/html')

              // Remove high-level elements that Word/Outlook often include and that aren't needed
              ;['style', 'meta', 'link'].forEach(tag => {
                const els = Array.from(doc.getElementsByTagName(tag))
                els.forEach(e => e.remove())
              })

              // Remove XML/namespace or proprietary Office tags like <o:p>
              Array.from(doc.querySelectorAll('*')).forEach((e) => {
                const tagName = e.tagName || ''
                if (tagName.indexOf(':') !== -1) {
                  e.remove()
                }
              })

              const elements2 = Array.from(doc.querySelectorAll('*')) as HTMLElement[]
              elements2.forEach((el) => {
                const tag = el.tagName.toLowerCase()

                // Inspect inline style first to preserve semantic formatting marks
                const styleAttr = el.getAttribute('style') || ''
                if (styleAttr) {
                  // Build a map of style properties
                  const styleMap: Record<string, string> = {}
                  styleAttr.split(';').forEach(s => {
                    const [k, v] = s.split(':').map(p => p && p.trim())
                    if (k && v) styleMap[k.toLowerCase()] = v
                  })

                  // If the element uses inline styles to indicate bold/italic/underline,
                  // convert those to semantic elements so TipTap/ProseMirror will keep them.
                  const shouldWrapStrong = Boolean(styleMap['font-weight'] && (/bold/i.test(styleMap['font-weight']) || Number(styleMap['font-weight']) >= 600))
                  const shouldWrapEm = Boolean(styleMap['font-style'] && /italic/i.test(styleMap['font-style']))
                  const textDecoration = styleMap['text-decoration'] || styleMap['text-decoration-line'] || ''
                  const shouldWrapUnderline = /underline/i.test(textDecoration)

                  function wrapChildrenWith(tagName: string) {
                    // don't double-wrap if the element itself is already the tag
                    if (el.tagName.toLowerCase() === tagName) return
                    // don't wrap if children already include the tag as a direct child
                    if ([...el.children].some(c => c.tagName.toLowerCase() === tagName)) return
                    const wrapper = doc.createElement(tagName)
                    while (el.firstChild) wrapper.appendChild(el.firstChild)
                    el.appendChild(wrapper)
                  }

                  if (shouldWrapStrong) wrapChildrenWith('strong')
                  if (shouldWrapEm) wrapChildrenWith('em')
                  if (shouldWrapUnderline) wrapChildrenWith('u')
                }

                // After converting style-based emphasis to semantic tags, aggressively remove
                // all inline styles to avoid any residual font/color/spacing from Word.
                if (el.hasAttribute('style')) el.removeAttribute('style')

                // Remove Word-specific data attributes and ARIA/role attributes that carry layout info
                Array.from(el.getAttributeNames()).forEach(attrName => {
                  if (/^data-/i.test(attrName)) el.removeAttribute(attrName)
                  if (/^aria-/i.test(attrName)) el.removeAttribute(attrName)
                  if (attrName === 'role') el.removeAttribute('role')
                })

                // Remove Word Online / Office-specific classes (SCXW*, BCX*, and other generated classes)
                // Keep developer/semantic classes but drop Word's markup classes which are noisy.
                if (el.hasAttribute('class')) {
                  const classes = (el.getAttribute('class') || '').split(/\s+/).filter(Boolean)
                  const wordClassPattern = /^(SCXW|BCX|ListContainerWrapper|BulletListStyle|OutlineElement|Paragraph|TextRun|NormalTextRun|EOP|SpellingError|Underlined|ContextualSpelling|Ltr|BCX)\b/i
                  const kept = classes.filter(c => !wordClassPattern.test(c))
                  if (kept.length > 0) el.setAttribute('class', kept.join(' '))
                  else el.removeAttribute('class')
                }

                // Remove deprecated attributes on <font> or other tags
                if (el.hasAttribute('color')) el.removeAttribute('color')
                if (el.hasAttribute('size')) el.removeAttribute('size')
                if (el.hasAttribute('face')) el.removeAttribute('face')

                // Unwrap <font> tags but keep children
                if (tag === 'font') {
                  const parent = el.parentNode
                  while (el.firstChild) parent?.insertBefore(el.firstChild, el)
                  parent?.removeChild(el)
                }

                // Normalize list-related inline styles which commonly cause extra spacing
                if (tag === 'ul' || tag === 'ol' || tag === 'li') {
                  const listStyle = (el.getAttribute('style') || '')
                    .split(';')
                    .map(s => s.trim())
                    .filter(s => s.length > 0 && !/^(?:margin|margin-top|margin-bottom|padding|padding-left|padding-right|line-height)\s*:/i.test(s))
                    .join('; ')
                  if (listStyle.length > 0) {
                    el.setAttribute('style', listStyle)
                  } else {
                    el.removeAttribute('style')
                  }
                }

                // Unwrap a single <p> or single-block wrapper inside an <li> to avoid doubled spacing
                if (tag === 'li') {
                  const firstChild = el.firstElementChild as HTMLElement | null
                  if (firstChild && ['p', 'div'].includes(firstChild.tagName.toLowerCase())) {
                    // Move children of the wrapper up into the <li>
                    while (firstChild.firstChild) el.insertBefore(firstChild.firstChild, firstChild)
                    firstChild.remove()
                  }
                }
              })

              // Convert cleaned HTML to a ProseMirror document using the editor schema
              const pmDoc = ProseMirrorDOMParser.fromSchema(view.state.schema).parse(doc.body)

              // Remove leading empty paragraph(s) that some paste sources include
              const nodes: any[] = []
              pmDoc.content.forEach((child) => nodes.push(child))
              // If the first node is an empty paragraph, drop it
              if (nodes.length > 0) {
                const first = nodes[0]
                // Remove leading empty text nodes or empty paragraphs
                if ((first.isText && (!first.text || first.text.trim() === '')) || (first.type && first.type.name === 'paragraph' && first.content.size === 0)) {
                  nodes.shift()
                }
              }

              const cleanedFragment = Fragment.fromArray(nodes)
              const slice = new Slice(cleanedFragment, 0, 0)
              const tr = view.state.tr.replaceSelection(slice)
              view.dispatch(tr.scrollIntoView())

              // We handled the paste
              return true
            } catch (err) {
              // If anything goes wrong, allow default handling
              // console.error('PasteCleanupExtension error:', err)
              return false
            }
          },
        },
      }),
    ]
  },
})

export default PasteCleanupExtension